import { createId } from '../lib/ids';
import { CHARACTER_ASPECT, CHARACTER_PRESETS, type CharacterPresetId } from '../scene/characters';
import { copySceneAnimations, pruneAnimations } from './animationRefs';
import { createLayer, sceneEditorDefaults } from './factories';
import { OpError } from './projectOps';
import {
  CharacterSchema,
  MAX_ANIMATIONS,
  MAX_CHARACTERS,
  MAX_LAYERS,
  MAX_SCENES,
  MAX_SCENE_OBJECTS,
  OBJECT_MAX_SIZE,
  SCENE_COORD_LIMIT,
  SCENE_MAX_SIZE,
  SCENE_MIN_SIZE,
  SceneSchema,
  formatIssues,
  type Character,
  type Layer,
  type Project,
  type Scene,
  type SceneObject,
  type SceneObjectType,
} from './schemas';

/**
 * Pure, validated operations for the Phase 3A scene editor. Every function returns a new
 * Project or throws OpError and changes nothing. Each changed scene (and character) is
 * re-validated against its Zod schema before it is returned, so an operation can never
 * produce a scene that would fail to save.
 *
 * Coordinates are scene pixels: x/y is the top-left of an object's box before rotation;
 * rotation (degrees) turns the box around its anchor (a fraction of the box).
 */

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round2 = (v: number) => Math.round(v * 100) / 100;
const finite = (v: number, label: string) => {
  if (!Number.isFinite(v)) throw new OpError(`${label} must be a number.`);
  return v;
};

// ---------------------------------------------------------------- Lookup and validation

export function sceneOf(project: Project, sceneId: string): Scene {
  const scene = project.scenes.find((s) => s.id === sceneId);
  if (!scene) throw new OpError('That scene no longer exists.');
  return scene;
}

export const activeSceneOf = (project: Project): Scene => sceneOf(project, project.activeSceneId);

export function objectOf(scene: Scene, objectId: string): SceneObject {
  const object = scene.objects.find((o) => o.id === objectId);
  if (!object) throw new OpError('That object no longer exists.');
  return object;
}

export function layerOf(scene: Scene, layerId: string): Layer {
  const layer = scene.layers.find((l) => l.id === layerId);
  if (!layer) throw new OpError('That layer no longer exists.');
  return layer;
}

/** True when an object can't be edited: it, or its layer, is locked. */
export function isLocked(scene: Scene, object: SceneObject): boolean {
  return object.locked || !!scene.layers.find((l) => l.id === object.layerId)?.locked;
}

/** True when an object is drawn: it and its layer are visible. */
export function isShown(scene: Scene, object: SceneObject): boolean {
  return object.visible && !!scene.layers.find((l) => l.id === object.layerId)?.visible;
}

function editable(scene: Scene, objectId: string): SceneObject {
  const object = objectOf(scene, objectId);
  if (object.locked) throw new OpError(`“${object.name}” is locked. Unlock it first.`);
  if (layerOf(scene, object.layerId).locked) throw new OpError(`“${object.name}” is on a locked layer. Unlock the layer first.`);
  return object;
}

/** Validates a changed scene; returns the project with it replaced. */
function withScene(project: Project, scene: Scene): Project {
  const parsed = SceneSchema.safeParse(scene);
  if (!parsed.success) throw new OpError(`That change would make the scene invalid: ${formatIssues(parsed.error)[0] ?? 'unknown problem'}`);
  if (!project.scenes.some((s) => s.id === scene.id)) throw new OpError('That scene no longer exists.');
  return { ...project, scenes: project.scenes.map((s) => (s.id === scene.id ? scene : s)) };
}

const mapObjects = (scene: Scene, ids: ReadonlySet<string>, fn: (o: SceneObject) => SceneObject): Scene => ({
  ...scene,
  objects: scene.objects.map((o) => (ids.has(o.id) ? fn(o) : o)),
});

// ---------------------------------------------------------------- Scenes

export function setActiveScene(project: Project, sceneId: string): Project {
  sceneOf(project, sceneId);
  return project.activeSceneId === sceneId ? project : { ...project, activeSceneId: sceneId };
}

/** Adds an empty scene (or the given fully built scene) and makes it active. */
export function addScene(project: Project, scene?: Scene, name = 'New scene'): Project {
  if (project.scenes.length >= MAX_SCENES) throw new OpError(`A project can hold at most ${MAX_SCENES} scenes.`);
  const next: Scene = scene ?? { id: createId('scn'), name, background: '#111827', speed: 1, elements: [], ...sceneEditorDefaults() };
  if (project.scenes.some((s) => s.id === next.id)) throw new OpError('A scene with that id already exists.');
  const parsed = SceneSchema.safeParse(next);
  if (!parsed.success) throw new OpError(`The new scene is invalid: ${formatIssues(parsed.error)[0]}`);
  return { ...project, scenes: [...project.scenes, next], activeSceneId: next.id };
}

export function renameScene(project: Project, sceneId: string, name: string): Project {
  const trimmed = name.trim().slice(0, 80);
  if (!trimmed) throw new OpError('A scene name cannot be empty.');
  return withScene(project, { ...sceneOf(project, sceneId), name: trimmed });
}

/**
 * Copies a scene with fresh scene, layer and object ids (characters and images are
 * shared). The scene's animations are copied too, retargeted to the copied objects.
 */
export function duplicateScene(project: Project, sceneId: string, newId = createId('scn')): Project {
  const source = sceneOf(project, sceneId);
  const layerIds = new Map(source.layers.map((l) => [l.id, createId('lyr')]));
  const objectIds = new Map(source.objects.map((o) => [o.id, createId('obj')]));
  const groupIds = new Map<string, string>();
  const copy: Scene = {
    ...structuredClone(source),
    id: newId,
    name: `${source.name} copy`.slice(0, 80),
    layers: source.layers.map((l) => ({ ...l, id: layerIds.get(l.id)! })),
    objects: source.objects.map((o) => ({
      ...structuredClone(o),
      id: objectIds.get(o.id)!,
      layerId: layerIds.get(o.layerId)!,
      groupId: o.groupId ? (groupIds.get(o.groupId) ?? groupIds.set(o.groupId, createId('grp')).get(o.groupId)!) : null,
    })),
    elements: source.elements.map((e) => ({ ...e, id: createId('el') })),
  };
  const next = addScene(project, copy);
  const animations = copySceneAnimations(project, sceneId, newId, objectIds);
  if (project.animations.length + animations.length > MAX_ANIMATIONS) throw new OpError(`A project can hold at most ${MAX_ANIMATIONS} animations.`);
  return animations.length ? { ...next, animations: [...next.animations, ...animations] } : next;
}

/** Deletes a scene together with its animations. */
export function deleteScene(project: Project, sceneId: string): Project {
  sceneOf(project, sceneId);
  if (project.scenes.length <= 1) throw new OpError('A project needs at least one scene.');
  const scenes = project.scenes.filter((s) => s.id !== sceneId);
  return pruneAnimations({ ...project, scenes, activeSceneId: project.activeSceneId === sceneId ? scenes[0]!.id : project.activeSceneId });
}

export function setSceneSize(project: Project, sceneId: string, width: number, height: number): Project {
  const w = Math.round(clamp(finite(width, 'Width'), SCENE_MIN_SIZE, SCENE_MAX_SIZE));
  const h = Math.round(clamp(finite(height, 'Height'), SCENE_MIN_SIZE, SCENE_MAX_SIZE));
  return withScene(project, { ...sceneOf(project, sceneId), width: w, height: h });
}

export function setSceneBackground(project: Project, sceneId: string, patch: Partial<Pick<Scene, 'background' | 'gradient'>>): Project {
  return withScene(project, { ...sceneOf(project, sceneId), ...patch });
}

/**
 * Phase 5B.1: sets the scene's colour grade and/or vignette (null removes one). A look with
 * neither is stored as absent, so a cleared look leaves the scene exactly as before.
 */
export function setSceneLook(project: Project, sceneId: string, patch: Partial<NonNullable<Scene['look']>>): Project {
  const scene = sceneOf(project, sceneId);
  const look = { ...(scene.look ?? {}), ...patch };
  const next: Scene = { ...scene, look: look.grade || look.vignette ? { grade: look.grade ?? null, vignette: look.vignette ?? null } : undefined };
  if (next.look === undefined) delete (next as { look?: unknown }).look;
  return withScene(project, next);
}

export function setBackgroundImage(project: Project, sceneId: string, image: Scene['backgroundImage']): Project {
  if (image) imageAssetOf(project, image.assetId);
  return withScene(project, { ...sceneOf(project, sceneId), backgroundImage: image });
}

// ---------------------------------------------------------------- Layers

export function addLayer(project: Project, sceneId: string, name?: string, id = createId('lyr')): Project {
  const scene = sceneOf(project, sceneId);
  if (scene.layers.length >= MAX_LAYERS) throw new OpError(`A scene can have at most ${MAX_LAYERS} layers.`);
  return withScene(project, { ...scene, layers: [...scene.layers, createLayer(name?.trim() || `Layer ${scene.layers.length + 1}`, id)] });
}

export function updateLayer(project: Project, sceneId: string, layerId: string, patch: Partial<Omit<Layer, 'id'>>): Project {
  const scene = sceneOf(project, sceneId);
  const layer = layerOf(scene, layerId);
  const name = patch.name !== undefined ? patch.name.trim().slice(0, 60) : layer.name;
  if (!name) throw new OpError('A layer name cannot be empty.');
  return withScene(project, { ...scene, layers: scene.layers.map((l) => (l.id === layerId ? { ...l, ...patch, name } : l)) });
}

/** Moves a layer up (+1, towards the front) or down (−1). */
export function moveLayer(project: Project, sceneId: string, layerId: string, delta: number): Project {
  const scene = sceneOf(project, sceneId);
  const index = scene.layers.findIndex((l) => l.id === layerId);
  if (index < 0) throw new OpError('That layer no longer exists.');
  const target = clamp(index + Math.sign(delta), 0, scene.layers.length - 1);
  if (target === index) return project;
  const layers = [...scene.layers];
  const [layer] = layers.splice(index, 1);
  layers.splice(target, 0, layer!);
  return withScene(project, { ...scene, layers });
}

/** Deletes a layer. Its objects move to the layer below (or above, for the bottom layer). */
export function deleteLayer(project: Project, sceneId: string, layerId: string): Project {
  const scene = sceneOf(project, sceneId);
  const index = scene.layers.findIndex((l) => l.id === layerId);
  if (index < 0) throw new OpError('That layer no longer exists.');
  if (scene.layers.length <= 1) throw new OpError('A scene needs at least one layer.');
  if (scene.layers[index]!.locked) throw new OpError('That layer is locked. Unlock it first.');
  const fallback = scene.layers[index === 0 ? 1 : index - 1]!;
  return withScene(project, {
    ...scene,
    layers: scene.layers.filter((l) => l.id !== layerId),
    objects: scene.objects.map((o) => (o.layerId === layerId ? { ...o, layerId: fallback.id } : o)),
  });
}

export function moveObjectsToLayer(project: Project, sceneId: string, ids: readonly string[], layerId: string): Project {
  const scene = sceneOf(project, sceneId);
  const target = layerOf(scene, layerId);
  if (target.locked) throw new OpError(`The layer “${target.name}” is locked.`);
  const set = new Set(ids);
  for (const id of set) editable(scene, id);
  // Moved objects go on top of their new layer, keeping their relative order.
  const moved = scene.objects.filter((o) => set.has(o.id)).map((o) => ({ ...o, layerId }));
  const rest = scene.objects.filter((o) => !set.has(o.id));
  return withScene(project, { ...scene, objects: [...rest, ...moved] });
}

// ---------------------------------------------------------------- Objects

export type NewObject =
  | { type: 'rect' | 'circle' | 'ellipse'; fill?: string }
  | { type: 'polygon'; fill?: string; points?: [number, number][] }
  | { type: 'text'; text?: string; color?: string }
  | { type: 'image'; assetId: string }
  | { type: 'character'; characterId: string };

const TYPE_LABELS: Record<SceneObjectType, string> = {
  rect: 'Rectangle',
  circle: 'Circle',
  ellipse: 'Ellipse',
  polygon: 'Polygon',
  text: 'Text',
  image: 'Image',
  character: 'Character',
};

export const TRIANGLE: [number, number][] = [
  [0.5, 0],
  [1, 1],
  [0, 1],
];

/** Top layer that isn't locked (new objects go there), or the given layer. */
function targetLayer(scene: Scene, layerId?: string): Layer {
  if (layerId) {
    const layer = layerOf(scene, layerId);
    if (layer.locked) throw new OpError(`The layer “${layer.name}” is locked.`);
    return layer;
  }
  const layer = [...scene.layers].reverse().find((l) => !l.locked && l.visible) ?? [...scene.layers].reverse().find((l) => !l.locked);
  if (!layer) throw new OpError('Every layer is locked. Unlock a layer to add objects.');
  return layer;
}

/** Creates a fully specified object of a type, centred in the scene unless placed. */
export function buildObject(project: Project, scene: Scene, spec: NewObject, place: { x?: number; y?: number; width?: number; height?: number; layerId?: string; id?: string } = {}): SceneObject {
  const layer = targetLayer(scene, place.layerId);
  const count = scene.objects.filter((o) => o.type === spec.type).length + 1;
  let width = place.width ?? 200;
  let height = place.height ?? 200;
  if (spec.type === 'ellipse' && place.height === undefined) height = 130;
  if (spec.type === 'text' && place.height === undefined) [width, height] = [360, 80];
  if (spec.type === 'image') {
    const asset = imageAssetOf(project, spec.assetId);
    const w = asset.image?.width ?? 400;
    const h = asset.image?.height ?? 300;
    const fit = Math.min(1, (scene.width * 0.6) / w, (scene.height * 0.6) / h);
    width = place.width ?? Math.max(1, Math.round(w * fit));
    height = place.height ?? Math.max(1, Math.round(h * fit));
  }
  if (spec.type === 'character') {
    const character = characterOf(project, spec.characterId);
    height = place.height ?? Math.round(Math.min(scene.height * 0.6, 360));
    width = place.width ?? Math.round(height * CHARACTER_ASPECT);
    void character;
  }
  const base = {
    id: place.id ?? createId('obj'),
    name: spec.type === 'character' ? characterOf(project, spec.characterId).name : `${TYPE_LABELS[spec.type]} ${count}`,
    layerId: layer.id,
    x: round2(place.x ?? (scene.width - width) / 2),
    y: round2(place.y ?? (scene.height - height) / 2),
    width,
    height,
    rotation: 0,
    opacity: 1,
    anchorX: 0.5,
    anchorY: 0.5,
    visible: true,
    locked: false,
    groupId: null,
  };
  switch (spec.type) {
    case 'rect':
      return { ...base, type: 'rect', style: { fill: spec.fill ?? '#22d3ee', stroke: null }, cornerRadius: 0 };
    case 'circle':
      return { ...base, type: 'circle', style: { fill: spec.fill ?? '#f472b6', stroke: null } };
    case 'ellipse':
      return { ...base, type: 'ellipse', style: { fill: spec.fill ?? '#a78bfa', stroke: null } };
    case 'polygon':
      return { ...base, type: 'polygon', style: { fill: spec.fill ?? '#facc15', stroke: null }, points: spec.points ?? TRIANGLE };
    case 'text':
      return { ...base, type: 'text', text: spec.text ?? 'Text', fontSize: 48, fontFamily: 'sans', bold: true, align: 'center', color: spec.color ?? '#ffffff' };
    case 'image':
      return { ...base, type: 'image', assetId: spec.assetId, fit: 'stretch' };
    case 'character':
      return { ...base, type: 'character', characterId: spec.characterId, flipX: false };
  }
}

export function addObject(project: Project, sceneId: string, object: SceneObject): Project {
  const scene = sceneOf(project, sceneId);
  if (scene.objects.length >= MAX_SCENE_OBJECTS) throw new OpError(`A scene can hold at most ${MAX_SCENE_OBJECTS} objects.`);
  if (scene.objects.some((o) => o.id === object.id)) throw new OpError('An object with that id already exists.');
  const layer = layerOf(scene, object.layerId);
  if (layer.locked) throw new OpError(`The layer “${layer.name}” is locked.`);
  if (object.type === 'image') imageAssetOf(project, object.assetId);
  if (object.type === 'character') characterOf(project, object.characterId);
  return withScene(project, { ...scene, objects: [...scene.objects, object] });
}

/** Shorthand: build and add an object of a type; returns the project and the new id. */
export function createObject(project: Project, sceneId: string, spec: NewObject, place: Parameters<typeof buildObject>[3] = {}): { project: Project; id: string } {
  const object = buildObject(project, sceneOf(project, sceneId), spec, place);
  return { project: addObject(project, sceneId, object), id: object.id };
}

export type ObjectPatch = Partial<Record<string, unknown>> & Partial<Omit<SceneObject, 'id' | 'type'>>;

const TRANSFORM_KEYS = new Set(['x', 'y', 'width', 'height', 'rotation', 'opacity', 'anchorX', 'anchorY']);

/** Clamps transform values into their valid ranges (NaN is rejected). */
function cleanTransform(patch: ObjectPatch): ObjectPatch {
  const out: ObjectPatch = { ...patch };
  for (const key of TRANSFORM_KEYS) {
    if (!(key in patch)) continue;
    const v = finite(Number(patch[key as keyof ObjectPatch]), key);
    if (key === 'x' || key === 'y') out[key] = round2(clamp(v, -SCENE_COORD_LIMIT, SCENE_COORD_LIMIT));
    else if (key === 'width' || key === 'height') out[key] = round2(clamp(v, 1, OBJECT_MAX_SIZE));
    else if (key === 'rotation') out[key] = round2(((((v + 180) % 360) + 360) % 360) - 180);
    else out[key] = Math.round(clamp(v, 0, 1) * 1000) / 1000;
  }
  return out;
}

/**
 * Updates objects (transform, style or type-specific fields). Locked objects are refused.
 * The patch is validated per object; `style` patches merge into the existing style.
 */
export function updateObjects(project: Project, sceneId: string, ids: readonly string[], patch: ObjectPatch | ((o: SceneObject) => ObjectPatch)): Project {
  const scene = sceneOf(project, sceneId);
  const set = new Set(ids);
  if (set.size === 0) throw new OpError('Nothing is selected.');
  for (const id of set) editable(scene, id);
  if (typeof patch !== 'function' && 'layerId' in patch) throw new OpError('Use “move to layer” to change an object’s layer.');
  const next = mapObjects(scene, set, (o) => {
    const raw = typeof patch === 'function' ? patch(o) : patch;
    const p = cleanTransform(raw);
    const merged = { ...o, ...p } as SceneObject;
    if ('style' in o && p.style) (merged as { style: unknown }).style = { ...o.style, ...(p.style as object) };
    if (o.type === 'circle' && ('width' in p || 'height' in p)) {
      const d = 'width' in p && !('height' in p) ? merged.width : 'height' in p && !('width' in p) ? merged.height : Math.max(merged.width, merged.height);
      merged.width = d;
      merged.height = d;
    }
    if (merged.type === 'image' && 'assetId' in p) imageAssetOf(project, merged.assetId);
    if (merged.type === 'character' && 'characterId' in p) characterOf(project, merged.characterId);
    return merged;
  });
  return withScene(project, next);
}

/** Moves objects by a delta in scene pixels. */
export function nudgeObjects(project: Project, sceneId: string, ids: readonly string[], dx: number, dy: number): Project {
  finite(dx, 'Horizontal offset');
  finite(dy, 'Vertical offset');
  return updateObjects(project, sceneId, ids, (o) => ({ x: o.x + dx, y: o.y + dy }));
}

/** Scales objects around their centre by `factor`. */
export function scaleObjects(project: Project, sceneId: string, ids: readonly string[], factor: number): Project {
  if (!Number.isFinite(factor) || factor <= 0) throw new OpError('The scale factor must be a positive number.');
  return updateObjects(project, sceneId, ids, (o) => {
    const width = clamp(o.width * factor, 1, OBJECT_MAX_SIZE);
    const height = clamp(o.height * factor, 1, OBJECT_MAX_SIZE);
    return { x: o.x - (width - o.width) * o.anchorX, y: o.y - (height - o.height) * o.anchorY, width, height };
  });
}

export function setVisible(project: Project, sceneId: string, ids: readonly string[], visible: boolean): Project {
  const scene = sceneOf(project, sceneId);
  const set = new Set(ids);
  for (const id of set) editable(scene, id);
  return withScene(project, mapObjects(scene, set, (o) => ({ ...o, visible })));
}

/** Locking and unlocking are always allowed (unlocking is how a locked object becomes editable). */
export function setLocked(project: Project, sceneId: string, ids: readonly string[], locked: boolean): Project {
  const scene = sceneOf(project, sceneId);
  const set = new Set(ids);
  for (const id of set) objectOf(scene, id);
  return withScene(project, mapObjects(scene, set, (o) => ({ ...o, locked })));
}

export function renameObject(project: Project, sceneId: string, id: string, name: string): Project {
  const trimmed = name.trim().slice(0, 60);
  if (!trimmed) throw new OpError('A name cannot be empty.');
  return updateObjects(project, sceneId, [id], { name: trimmed });
}

/** Duplicates objects 20 px down-right; group membership is copied into a new group. */
export function duplicateObjects(project: Project, sceneId: string, ids: readonly string[], idFor: (old: string) => string = () => createId('obj')): { project: Project; ids: string[] } {
  const scene = sceneOf(project, sceneId);
  const set = new Set(ids);
  if (set.size === 0) throw new OpError('Nothing is selected.');
  for (const id of set) editable(scene, id);
  if (scene.objects.length + set.size > MAX_SCENE_OBJECTS) throw new OpError(`A scene can hold at most ${MAX_SCENE_OBJECTS} objects.`);
  const groups = new Map<string, string>();
  const copies: SceneObject[] = [];
  const objects: SceneObject[] = [];
  for (const o of scene.objects) {
    objects.push(o);
    if (!set.has(o.id)) continue;
    const groupId = o.groupId ? (groups.get(o.groupId) ?? groups.set(o.groupId, createId('grp')).get(o.groupId)!) : null;
    const copy = { ...structuredClone(o), id: idFor(o.id), name: `${o.name} copy`.slice(0, 60), x: o.x + 20, y: o.y + 20, groupId };
    copies.push(copy);
    objects.push(copy);
  }
  return { project: withScene(project, { ...scene, objects }), ids: copies.map((c) => c.id) };
}

export function deleteObjects(project: Project, sceneId: string, ids: readonly string[]): Project {
  const scene = sceneOf(project, sceneId);
  const set = new Set(ids);
  if (set.size === 0) throw new OpError('Nothing is selected.');
  for (const id of set) editable(scene, id);
  // Their animation tracks go with them (atomically, in the same project change).
  return pruneAnimations(withScene(project, { ...scene, objects: scene.objects.filter((o) => !set.has(o.id)) }));
}

export type ZOrder = 'front' | 'back' | 'forward' | 'backward';

/** Reorders objects within their layer (layers themselves are ordered separately). */
export function reorderObjects(project: Project, sceneId: string, ids: readonly string[], where: ZOrder): Project {
  const scene = sceneOf(project, sceneId);
  const set = new Set(ids);
  for (const id of set) editable(scene, id);
  let objects = [...scene.objects];
  const layerIds = new Set(objects.filter((o) => set.has(o.id)).map((o) => o.layerId));
  for (const layerId of layerIds) {
    // Positions in the global array that belong to this layer, in draw order.
    const slots = objects.map((o, i) => (o.layerId === layerId ? i : -1)).filter((i) => i >= 0);
    const inLayer = slots.map((i) => objects[i]!);
    let ordered = inLayer;
    if (where === 'front') ordered = [...inLayer.filter((o) => !set.has(o.id)), ...inLayer.filter((o) => set.has(o.id))];
    else if (where === 'back') ordered = [...inLayer.filter((o) => set.has(o.id)), ...inLayer.filter((o) => !set.has(o.id))];
    else {
      ordered = [...inLayer];
      const step = where === 'forward' ? 1 : -1;
      const indices = ordered.map((o, i) => (set.has(o.id) ? i : -1)).filter((i) => i >= 0);
      if (step > 0) indices.reverse();
      for (const i of indices) {
        const j = i + step;
        if (j < 0 || j >= ordered.length || set.has(ordered[j]!.id)) continue;
        [ordered[i], ordered[j]] = [ordered[j]!, ordered[i]!];
      }
    }
    objects = [...objects];
    slots.forEach((slot, k) => (objects[slot] = ordered[k]!));
  }
  return withScene(project, { ...scene, objects });
}

export function groupObjects(project: Project, sceneId: string, ids: readonly string[], groupId = createId('grp')): Project {
  if (new Set(ids).size < 2) throw new OpError('Select at least two objects to group.');
  return updateObjects(project, sceneId, ids, { groupId });
}

export function ungroupObjects(project: Project, sceneId: string, ids: readonly string[]): Project {
  const scene = sceneOf(project, sceneId);
  const groups = new Set(ids.map((id) => objectOf(scene, id).groupId).filter((g): g is string => !!g));
  const members = scene.objects.filter((o) => o.groupId && groups.has(o.groupId)).map((o) => o.id);
  if (members.length === 0) return project;
  return updateObjects(project, sceneId, members, { groupId: null });
}

/** Expands a selection to whole groups (clicking one member selects the group). */
export function expandToGroups(scene: Scene, ids: readonly string[]): string[] {
  const groups = new Set(scene.objects.filter((o) => ids.includes(o.id) && o.groupId).map((o) => o.groupId));
  return scene.objects.filter((o) => ids.includes(o.id) || (o.groupId && groups.has(o.groupId))).map((o) => o.id);
}

// ---------------------------------------------------------------- Characters

export function characterOf(project: Project, characterId: string): Character {
  const character = project.characters.find((c) => c.id === characterId);
  if (!character) throw new OpError('That character no longer exists.');
  return character;
}

function withCharacter(project: Project, character: Character, isNew = false): Project {
  const parsed = CharacterSchema.safeParse(character);
  if (!parsed.success) throw new OpError(`That would make the character invalid: ${formatIssues(parsed.error)[0]}`);
  if (isNew) {
    if (project.characters.length >= MAX_CHARACTERS) throw new OpError(`A project can hold at most ${MAX_CHARACTERS} characters.`);
    return { ...project, characters: [...project.characters, parsed.data] };
  }
  characterOf(project, character.id);
  return { ...project, characters: project.characters.map((c) => (c.id === character.id ? parsed.data : c)) };
}

export function createCharacter(project: Project, preset: CharacterPresetId, id = createId('chr'), name?: string): Project {
  const base = structuredClone(CHARACTER_PRESETS[preset].character);
  const taken = new Set(project.characters.map((c) => c.name));
  let label = name?.trim() || base.name;
  for (let n = 2; taken.has(label); n++) label = `${base.name} ${n}`;
  return withCharacter(project, { ...base, id, name: label.slice(0, 60) }, true);
}

export type CharacterPatch = Partial<{
  [K in keyof Omit<Character, 'id'>]: Character[K] extends object ? Partial<Character[K]> | Character[K] : Character[K];
}>;

/** Edits a definition. Every instance in every scene picks up the change. */
export function updateCharacter(project: Project, characterId: string, patch: CharacterPatch): Project {
  const c = characterOf(project, characterId);
  const next: Character = {
    ...c,
    ...(patch as Partial<Character>),
    proportions: { ...c.proportions, ...patch.proportions },
    head: { ...c.head, ...patch.head },
    eyes: { ...c.eyes, ...patch.eyes },
    hair: { ...c.hair, ...patch.hair },
    clothing: { ...c.clothing, ...patch.clothing },
    accessories: (patch.accessories as Character['accessories'] | undefined) ?? c.accessories,
  };
  if (patch.name !== undefined) {
    next.name = patch.name.trim().slice(0, 60);
    if (!next.name) throw new OpError('A character name cannot be empty.');
  }
  return withCharacter(project, next);
}

export function duplicateCharacter(project: Project, characterId: string, id = createId('chr')): Project {
  const c = characterOf(project, characterId);
  return withCharacter(project, { ...structuredClone(c), id, name: `${c.name} copy`.slice(0, 60) }, true);
}

/** Every placed instance of a character, by scene. */
export function characterUsage(project: Project, characterId: string): { sceneId: string; sceneName: string; objectIds: string[] }[] {
  return project.scenes
    .map((s) => ({ sceneId: s.id, sceneName: s.name, objectIds: s.objects.filter((o) => o.type === 'character' && o.characterId === characterId).map((o) => o.id) }))
    .filter((u) => u.objectIds.length > 0);
}

/**
 * Deletes a character definition. Refused while instances exist unless `removeInstances`
 * is set, in which case every instance (including locked ones) is removed too.
 */
export function deleteCharacter(project: Project, characterId: string, removeInstances = false): Project {
  characterOf(project, characterId);
  const usage = characterUsage(project, characterId);
  if (usage.length > 0 && !removeInstances) {
    const count = usage.reduce((n, u) => n + u.objectIds.length, 0);
    throw new OpError(`This character is placed ${count} time${count === 1 ? '' : 's'} (in ${usage.map((u) => `“${u.sceneName}”`).join(', ')}). Remove those instances first, or delete them together.`);
  }
  return pruneAnimations({
    ...project,
    characters: project.characters.filter((c) => c.id !== characterId),
    scenes: project.scenes.map((s) => (usage.some((u) => u.sceneId === s.id) ? { ...s, objects: s.objects.filter((o) => !(o.type === 'character' && o.characterId === characterId)) } : s)),
  });
}

// ---------------------------------------------------------------- Images

export function imageAssetOf(project: Project, assetId: string) {
  const asset = project.assets.find((a) => a.id === assetId);
  if (!asset) throw new OpError('That image is no longer in the library.');
  if (asset.kind !== 'image') throw new OpError(`“${asset.name}” is not an image.`);
  return asset;
}

/** Where an image asset is used: image objects and scene backgrounds. */
export function imageUsage(project: Project, assetId: string): { objects: { sceneId: string; objectId: string }[]; backgrounds: string[] } {
  const objects: { sceneId: string; objectId: string }[] = [];
  const backgrounds: string[] = [];
  for (const s of project.scenes) {
    if (s.backgroundImage?.assetId === assetId) backgrounds.push(s.id);
    for (const o of s.objects) if (o.type === 'image' && o.assetId === assetId) objects.push({ sceneId: s.id, objectId: o.id });
  }
  return { objects, backgrounds };
}

/** Removes every use of an image (objects and backgrounds), used when deleting the file. */
export function detachImage(project: Project, assetId: string): Project {
  const usage = imageUsage(project, assetId);
  if (usage.objects.length === 0 && usage.backgrounds.length === 0) return project;
  return pruneAnimations({
    ...project,
    scenes: project.scenes.map((s) => ({
      ...s,
      backgroundImage: s.backgroundImage?.assetId === assetId ? null : s.backgroundImage,
      objects: s.objects.filter((o) => !(o.type === 'image' && o.assetId === assetId)),
    })),
  });
}

export function renameImageAsset(project: Project, assetId: string, name: string): Project {
  imageAssetOf(project, assetId);
  const trimmed = name.trim().slice(0, 200);
  if (!trimmed) throw new OpError('A name cannot be empty.');
  return { ...project, assets: project.assets.map((a) => (a.id === assetId ? { ...a, name: trimmed } : a)) };
}

/** Stable, canonical JSON of a scene (object order is draw order). Used to compare scenes. */
export function serializeScene(scene: Scene): string {
  const sortKeys = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(sortKeys)
      : value && typeof value === 'object'
        ? Object.fromEntries(Object.keys(value as object).sort().map((k) => [k, sortKeys((value as Record<string, unknown>)[k])]))
        : value;
  return JSON.stringify(sortKeys(scene));
}
